"use client";
import React, { useState } from "react";
import { featuresTableData } from "./FeaturesTableData";
import { ArrowRight } from "lucide-react";

const COLS = "grid-cols-[1.6fr_1fr_1fr_1fr_1fr]";
const PRO_BG = "bg-[#F1F2FC]";
const PAYMENTS_URL = "https://app.imagive.ai/users/payments";

const CheckIcon = () => (
  <span className=" items-center justify-center w-6 h-6 rounded-full inline-flex md:bg-[#0E0B1A] md:text-white text-black ">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M3 6.2l2.2 2.2L9 4.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const XMark = () => (
  <span className="text-[#9CA0A8] text-[18px] leading-none select-none">×</span>
);

const Chevron = ({ open }) => (
  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#E6E7EB] ml-2 shrink-0">
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className={`transition-transform duration-200 ${open ? "" : "rotate-180"}`}
    >
      <path
        d="M2 6.5l3-3 3 3"
        stroke="#6D6D6D"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const valueColor = (value) => {
  const v = String(value).toLowerCase();
  if (["advanced", "standard", "community"].includes(v))
    return "text-[#16A34A]";
  if (v === "limited") return "text-[#D97706]";
  if (["unlimited", "pro-level", "dedicated"].includes(v))
    return "text-[#7C3AED]";
  return "text-[#0E0B1A]";
};

const Cell = ({ value, columnKey, featureName }) => {
  if (value === true) return <CheckIcon />;
  if (value === false) return <XMark />;

  if (featureName === "Monthly AI Credits") {
    const isCustom = value.startsWith("Custom");
    const [head, ...rest] = value.split(" ");
    return (
      <div className="text-center">
        <div
          className={`text-[15px] font-semibold ${
            columnKey === "pro" ? "text-[#2D2D97]" : "text-[#0E0B1A]"
          }`}
        >
          {isCustom ? "Custom" : head}
        </div>
        <div className="text-[11px] text-[#6D6D6D] mt-0.5">
          {isCustom ? "volume pricing" : rest.join(" ")}
        </div>
      </div>
    );
  }

  if (columnKey === "pro") {
    return (
      <span className="text-[#2D2D97] font-medium text-[14px]">{value}</span>
    );
  }

  return (
    <span className={`text-[14px] font-normal ${valueColor(value)}`}>
      {value}
    </span>
  );
};

/* One plan column inside a mobile feature card: small label on top, value below */
const PlanCol = ({ label, value, columnKey, featureName }) => (
  <div className="flex flex-col gap-2">
    <span className="text-[11px] font-medium text-[#9CA0A8]">{label}</span>
    <div className="text-[13px] leading-tight">
      <Cell value={value} columnKey={columnKey} featureName={featureName} />
    </div>
  </div>
);

const FeaturesPlansCompare = () => {
  const [openSections, setOpenSections] = useState(() =>
    featuresTableData.reduce((acc, s) => ({ ...acc, [s.category]: true }), {}),
  );

  const toggle = (category) =>
    setOpenSections((prev) => ({ ...prev, [category]: !prev[category] }));

  return (
    <section className="bg-[#F5F5F7] py-14 lg:py-20 px-5 sm:px-6 lg:px-20 ">
      <header className="mb-8 text-center lg:mb-10">
        <h2 className="text-[24px] leading-[1.15] sm:text-[30px] md:text-[44px] lg:text-5xl 3xl:text-[64px] 4xl:text-[80px] font-medium tracking-[-0.5px] text-[#0E0B1A]">
          Compare Plans & Features
        </h2>
        <p className="text-[#4A5060] text-[14px] sm:text-[16px] lg:text-lg 3xl:text-xl 4xl:text-2xl max-w-[490px] mx-auto mt-4 sm:mt-6 font-normal tracking-[-0.3px]">
          Everything you need to create, test, and scale high-performing ads
        </p>
      </header>

      <div className="hidden lg:block overflow-x-auto pt-6">
        <div className="lg:w-[1100px] mx-auto max-w-full relative">
          <div
            className={`absolute -top-4 left-0 right-0 grid ${COLS} pointer-events-none z-10`}
          >
            <div />
            <div />
            <div />
            <div className="flex justify-center">
              <span className="bg-[#0E0B1A] text-white px-4 py-1.5 rounded-full text-[12px] font-medium tracking-wide pointer-events-auto">
                Most Popular
              </span>
            </div>
            <div />
          </div>

          <div className={`grid ${COLS}`}>
            <div className="bg-white rounded-tl-2xl px-6 py-5 text-[15px] font-semibold text-[#0E0B1A]">
              Features
            </div>
            <div className="bg-white px-4 py-5 text-[15px] font-semibold text-[#0E0B1A] text-center">
              Free
            </div>
            <div className="bg-white px-4 py-5 text-[15px] font-semibold text-[#0E0B1A] text-center">
              Starter
            </div>
            <div
              className={`${PRO_BG} px-4 py-5 text-[15px] font-semibold text-[#0E0B1A] text-center`}
            >
              Pro
            </div>
            <div className="bg-white rounded-tr-2xl px-4 py-5 text-[15px] font-semibold text-[#0E0B1A] text-center">
              Business
            </div>
          </div>

          {featuresTableData.map((section) => {
            const isOpen = openSections[section.category];
            return (
              <React.Fragment key={section.category}>
                <div
                  className={`grid ${COLS} cursor-pointer`}
                  onClick={() => toggle(section.category)}
                >
                  <div className="px-6 py-4 text-[11px] uppercase tracking-[0.12em] text-[#6D6D6D] font-medium flex items-center">
                    <span>{section.category}</span>
                    <Chevron open={isOpen} />
                  </div>
                  <div />
                  <div />
                  <div className={PRO_BG} />
                  <div />
                </div>

                {isOpen &&
                  section.features.map((feature, featureIndex) => (
                    <div
                      key={feature.name}
                      className={`grid ${COLS} ${
                        featureIndex > 0 ? "border-t border-[#EDEDF0]" : ""
                      }`}
                    >
                      <div className="bg-white px-6 py-4 text-[14px] text-[#0E0B1A] flex items-center">
                        {feature.name}
                      </div>
                      <div className="bg-white px-4 py-4 flex items-center justify-center">
                        <Cell
                          value={feature.free}
                          columnKey="free"
                          featureName={feature.name}
                        />
                      </div>
                      <div className="bg-white px-4 py-4 flex items-center justify-center">
                        <Cell
                          value={feature.starter}
                          columnKey="starter"
                          featureName={feature.name}
                        />
                      </div>
                      <div
                        className={`${PRO_BG} px-4 py-4 flex items-center justify-center`}
                      >
                        <Cell
                          value={feature.pro}
                          columnKey="pro"
                          featureName={feature.name}
                        />
                      </div>
                      <div className="bg-white px-4 py-4 flex items-center justify-center">
                        <Cell
                          value={feature.business}
                          columnKey="business"
                          featureName={feature.name}
                        />
                      </div>
                    </div>
                  ))}
              </React.Fragment>
            );
          })}

          <div className={`grid ${COLS}`}>
            <div className="bg-white rounded-bl-2xl px-6 py-6 text-[11px] uppercase tracking-[0.12em] text-[#6D6D6D] font-medium flex items-center">
              READY TO GET STARTED?
            </div>
            <div className="bg-white px-3 py-6 flex justify-center">
              <a
                href={PAYMENTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center w-full max-w-[180px] text-[13px] font-medium"
              >
                Get Started Free
              </a>
            </div>
            <div className="bg-white px-3 py-6 flex justify-center">
              <a
                href={PAYMENTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center w-full max-w-[180px] text-[13px] font-medium"
              >
                Upgrade to Starter
              </a>
            </div>
            <div
              className={`${PRO_BG} px-3 py-6 flex justify-center rounded-b-2xl`}
            >
              <a
                href={PAYMENTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gradient group w-full max-w-[180px] text-[13px] font-medium inline-flex items-center justify-center gap-1.5"
              >
                Get Pro
                <ArrowRight
                  strokeWidth={1.8}
                  size={16}
                  className="group-hover:translate-x-2 duration-300 transition-all "
                />
              </a>
            </div>
            <div className="bg-white rounded-br-2xl px-3 py-6 flex justify-center">
              <a
                href={PAYMENTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center w-full max-w-[180px] text-[13px] font-medium"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile / tablet card view (below lg) ── */}
      <div className="lg:hidden mt-2 space-y-8">
        {featuresTableData.map((section) => {
          const isOpen = openSections[section.category];
          return (
            <div key={section.category}>
              <button
                type="button"
                onClick={() => toggle(section.category)}
                className="mb-3 flex w-full items-center justify-between px-1"
              >
                <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#6D6D6D]">
                  {section.category}
                </span>
                <Chevron open={isOpen} />
              </button>

              {isOpen && (
                <div className="space-y-3">
                  {section.features.map((feature) => (
                    <div
                      key={feature.name}
                      className="rounded-2xl bg-white p-5 shadow-[0_1px_3px_rgba(14,11,26,0.05)]"
                    >
                      <h4 className="text-[15px] font-semibold text-[#0E0B1A]">
                        {feature.name}
                      </h4>
                      <div className="mt-4 grid grid-cols-4 gap-2">
                        <PlanCol
                          label="Free"
                          value={feature.free}
                          columnKey="free"
                          featureName={feature.name}
                        />
                        <PlanCol
                          label="Starter"
                          value={feature.starter}
                          columnKey="starter"
                          featureName={feature.name}
                        />
                        <PlanCol
                          label="Pro"
                          value={feature.pro}
                          columnKey="pro"
                          featureName={feature.name}
                        />
                        <PlanCol
                          label="Business"
                          value={feature.business}
                          columnKey="business"
                          featureName={feature.name}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* CTA card */}
        <div className="rounded-2xl bg-white p-5 shadow-[0_1px_3px_rgba(14,11,26,0.05)]">
          <h4 className="mb-4 text-[11px] font-medium uppercase tracking-[0.12em] text-[#6D6D6D]">
            Ready to get started?
          </h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <a
              href={PAYMENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center w-full text-[13px] font-medium"
            >
              Get Started Free
            </a>
            <a
              href={PAYMENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center w-full text-[13px] font-medium"
            >
              Upgrade to Starter
            </a>
            <a
              href={PAYMENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gradient group inline-flex w-full items-center justify-center gap-1.5 text-[13px] font-medium"
            >
              Get Pro
              <ArrowRight
                strokeWidth={1.8}
                size={16}
                className="transition-all duration-300 group-hover:translate-x-2"
              />
            </a>
            <a
              href={PAYMENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center w-full text-[13px] font-medium"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesPlansCompare;
